/**
 * Which shell this device gets.
 *
 * PHONE = the mobile app's UI (bottom bar, phone-sized screens) rendered by
 * this web app; DESKTOP = the CRM as it has always been. The decision is made
 * ONCE per page load from the screen's shorter side plus a touch pointer
 * (orientation-proof: a phone turned sideways is still a phone, an iPad in
 * either orientation is not), so the router never has to be rebuilt for a
 * resize.
 *
 * `?shell=phone|desktop|auto` on any URL pins or clears the choice for this
 * browser (kept in localStorage) — how a desktop tester gets the phone shell
 * and how a phone user who really wants the full CRM can have it.
 */
export type ShellMode = "phone" | "desktop";

const OVERRIDE_KEY = "xale:shell";
const PHONE_MAX_SHORT_SIDE = 768;

let decided: boolean | null = null;

function readOverride(): ShellMode | null {
  try {
    const wanted = new URLSearchParams(window.location.search).get("shell");
    if (wanted === "phone" || wanted === "desktop") {
      localStorage.setItem(OVERRIDE_KEY, wanted);
      return wanted;
    }
    if (wanted === "auto") localStorage.removeItem(OVERRIDE_KEY);
    const stored = localStorage.getItem(OVERRIDE_KEY);
    return stored === "phone" || stored === "desktop" ? stored : null;
  } catch {
    return null;
  }
}

export function isPhoneShell(): boolean {
  if (decided != null) return decided;
  if (typeof window === "undefined") return false;
  const override = readOverride();
  if (override) {
    decided = override === "phone";
    return decided;
  }
  // A phone: a screen whose shorter side is under 768 AND a touch device (or
  // a window that narrow). The second half keeps a small-screened laptop, and
  // a headless browser whose `screen` reads 800×600, on the desktop CRM.
  const s = window.screen;
  const shortSide = Math.min(s?.width || window.innerWidth, s?.height || window.innerHeight);
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  decided = shortSide < PHONE_MAX_SHORT_SIDE && (coarse || window.innerWidth < PHONE_MAX_SHORT_SIDE);
  return decided;
}

/** Pin the other shell for this browser and reload into it. */
export function switchShell(mode: ShellMode | "auto"): void {
  try {
    if (mode === "auto") localStorage.removeItem(OVERRIDE_KEY);
    else localStorage.setItem(OVERRIDE_KEY, mode);
  } catch {
    /* storage off: the reload below still re-reads the screen */
  }
  const url = new URL(window.location.href);
  url.searchParams.delete("shell");
  window.location.replace(url.toString());
}
